import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Tooltip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  Avatar
} from '@mui/material';
import {
  Download as DownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
  Description as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  VideoFile as VideoIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import projectService, { ProjectDocument, ProjectDocumentFilters } from '../../services/projectService';

interface DocumentListProps {
  filters: ProjectDocumentFilters;
  onEditDocument: (document: ProjectDocument) => void;
  onRefresh: () => void;
}

const DocumentList: React.FC<DocumentListProps> = ({
  filters,
  onEditDocument,
  onRefresh
}) => {
  const [documents, setDocuments] = useState<ProjectDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedDocument, setSelectedDocument] = useState<ProjectDocument | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [downloading, setDownloading] = useState<number | null>(null);

  const loadDocuments = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await projectService.getProjectDocuments(
        filters,
        page * rowsPerPage,
        rowsPerPage
      );
      console.debug('DocumentList.loadDocuments: response', response);
      setDocuments(response.documents);
      setTotalCount(response.total_count);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al cargar los documentos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, [filters, page, rowsPerPage]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, document: ProjectDocument) => {
    setAnchorEl(event.currentTarget);
    setSelectedDocument(document);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDocument(null);
  };

  const handleDownload = async (doc: ProjectDocument) => {
    setDownloading(doc.id);
    try {
      const blob = await projectService.downloadProjectDocument(doc.id);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const linkElement = document.createElement('a');
      linkElement.href = url;
      linkElement.download = doc.original_file_name;
      document.body.appendChild(linkElement);
      linkElement.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(linkElement);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al descargar el documento');
    } finally {
      setDownloading(null);
    }
  };

  const handleDeleteClick = () => {
    // Open confirmation dialog but keep the selectedDocument so the dialog
    // can reference the target. Only close the menu anchor, do not clear
    // `selectedDocument` (calling handleMenuClose would clear it).
    setDeleteDialogOpen(true);
    setAnchorEl(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedDocument) return;

    setDeleting(true);
    console.debug('handleDeleteConfirm: deleting document id=', selectedDocument.id);
    try {
      await projectService.deleteProjectDocument(selectedDocument.id);
      console.debug('handleDeleteConfirm: delete succeeded id=', selectedDocument.id);
      setDeleteDialogOpen(false);
      setSelectedDocument(null);
      // Refresh parent and reload local list to ensure UI consistency
      onRefresh();
      console.debug('handleDeleteConfirm: calling loadDocuments after delete');
      await loadDocuments();
    } catch (err: any) {
      console.error('handleDeleteConfirm: delete failed', err);
      setError(err.response?.data?.detail || 'Error al eliminar el documento');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedDocument(null);
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) {
      return <ImageIcon color="primary" />;
    } else if (mimeType === 'application/pdf') {
      return <PdfIcon color="error" />;
    } else if (mimeType.startsWith('video/')) {
      return <VideoIcon color="secondary" />;
    }
    return <FileIcon color="action" />;
  };

  const getDocumentTypeLabel = (documentType: string) => {
    const labels: { [key: string]: string } = {
      brochure: 'Brochure',
      floor_plan: 'Plano',
      additional_info: 'Información Adicional',
      image: 'Imagen',
      video: 'Video',
      document: 'Documento'
    };
    return labels[documentType] || documentType;
  };

  const getDocumentTypeColor = (documentType: string) => {
    const colors: { [key: string]: 'primary' | 'secondary' | 'success' | 'warning' | 'info' | 'error' } = {
      brochure: 'primary',
      floor_plan: 'info',
      additional_info: 'secondary',
      image: 'success',
      video: 'warning',
      document: 'secondary'
    };
    return colors[documentType] || 'secondary';
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading && documents.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Archivo</TableCell>
                <TableCell>Proyecto</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Tamaño</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Subido por</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((document) => (
                <TableRow key={document.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar sx={{ width: 32, height: 32 }}>
                        {getFileIcon(document.mime_type)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium" noWrap sx={{ maxWidth: 200 }}>
                          {document.original_file_name}
                        </Typography>
                        {document.description && (
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                            {document.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {document.project_code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getDocumentTypeLabel(document.document_type)}
                      color={getDocumentTypeColor(document.document_type)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatFileSize(document.file_size)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={document.is_active ? 'Activo' : 'Inactivo'}
                      color={document.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {document.uploaded_by}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {format(new Date(document.uploaded_at), 'dd/MM/yyyy HH:mm', { locale: es })}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Descargar">
                      <IconButton
                        size="small"
                        onClick={() => handleDownload(document)}
                        disabled={downloading === document.id}
                      >
                        {downloading === document.id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <DownloadIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Más opciones">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, document)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {documents.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary" py={4}>
                      No se encontraron documentos
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />
      </Paper>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          if (selectedDocument) handleDownload(selectedDocument);
          handleMenuClose();
        }}>
          <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
          Descargar
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedDocument) onEditDocument(selectedDocument);
          handleMenuClose();
        }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Editar
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Eliminar
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar este documento?
            Esta acción no se puede deshacer.
          </Typography>
          {selectedDocument && (
            <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1}>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ width: 32, height: 32 }}>
                  {getFileIcon(selectedDocument.mime_type)}
                </Avatar>
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {selectedDocument.original_file_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedDocument.project_code} • {getDocumentTypeLabel(selectedDocument.document_type)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} /> : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentList;